
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { RestaurantDetails } from './details/RestaurantDetails';
import { HotelDetails } from './details/HotelDetails';
import { ActivityDetails } from './details/ActivityDetails';
import { FestivalDetails } from './details/FestivalDetails';
import { AttractionDetails } from './details/AttractionDetails';
import { CommonDetails } from './details/CommonDetails';

interface PlaceDetailsProps {
  note: string;
  setNote: (note: string) => void;
  url: string;
  setUrl: (url: string) => void;
  requiresReservation: boolean;
  setRequiresReservation: (requires: boolean) => void;
  cuisineType: string;
  setCuisineType: (type: string) => void;
  amenities: string;
  setAmenities: (amenities: string) => void;
  accessibilityFeatures: string;
  setAccessibilityFeatures: (features: string) => void;
  bestTimeToVisit: string;
  setBestTimeToVisit: (time: string) => void;
  tags: string;
  setTags: (tags: string) => void;
  address: string;
  setAddress: (address: string) => void;
  placeType: string;
  
  // Restaurant-specific props
  mealPriceRange: string;
  setMealPriceRange: (range: string) => void;
  cuisineSpecialties: string;
  setCuisineSpecialties: (specialties: string) => void;
  dressCode: string;
  setDressCode: (code: string) => void;
  outdoorSeating: boolean;
  setOutdoorSeating: (hasSeating: boolean) => void;
  
  // Hotel-specific props
  hotelClass: string;
  setHotelClass: (hotelClass: string) => void;
  checkInTime: string;
  setCheckInTime: (time: string) => void;
  checkOutTime: string;
  setCheckOutTime: (time: string) => void;
  hasPool: boolean;
  setHasPool: (hasPool: boolean) => void;
  hasSpa: boolean;
  setHasSpa: (hasSpa: boolean) => void;
  hasFitnessCenter: boolean;
  setHasFitnessCenter: (hasFitness: boolean) => void;
  hasRoomService: boolean;
  setHasRoomService: (hasService: boolean) => void;
  
  // Activity-specific props
  difficultyLevel: string;
  setDifficultyLevel: (level: string) => void;
  duration: string;
  setDuration: (duration: string) => void;
  equipmentNeeded: string;
  setEquipmentNeeded: (equipment: string) => void;
  maxGroupSize: string;
  setMaxGroupSize: (size: string) => void;
  minAge: string;
  setMinAge: (age: string) => void;
  suitableForChildren: boolean;
  setSuitableForChildren: (suitable: boolean) => void;
  weatherDependent: boolean;
  setWeatherDependent: (dependent: boolean) => void;
  
  // Festival/Event-specific props
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  frequency: string;
  setFrequency: (frequency: string) => void;
  ticketPrice: string;
  setTicketPrice: (price: string) => void;
  categories: string;
  setCategories: (categories: string) => void;
  capacity: string;
  setCapacity: (capacity: string) => void;
  familyFriendly: boolean;
  setFamilyFriendly: (friendly: boolean) => void;
  hasFood: boolean;
  setHasFood: (hasFood: boolean) => void;
  
  // Attraction-specific props
  entranceFee: string;
  setEntranceFee: (fee: string) => void;
  attractionType: string;
  setAttractionType: (type: string) => void;
  suggestedDuration: string;
  setSuggestedDuration: (duration: string) => void;
  exhibits: string;
  setExhibits: (exhibits: string) => void;
  historicalSignificance: string;
  setHistoricalSignificance: (significance: string) => void;
  isIndoor: boolean;
  setIsIndoor: (isIndoor: boolean) => void;
  
  isExistingPlace?: boolean;
}

export function PlaceDetails({
  note, setNote,
  url, setUrl,
  requiresReservation, setRequiresReservation,
  cuisineType, setCuisineType,
  amenities, setAmenities,
  accessibilityFeatures, setAccessibilityFeatures,
  bestTimeToVisit, setBestTimeToVisit,
  tags, setTags,
  address, setAddress,
  placeType,
  
  // Restaurant-specific props
  mealPriceRange, setMealPriceRange,
  cuisineSpecialties, setCuisineSpecialties,
  dressCode, setDressCode,
  outdoorSeating, setOutdoorSeating,
  
  // Hotel-specific props
  hotelClass, setHotelClass,
  checkInTime, setCheckInTime,
  checkOutTime, setCheckOutTime,
  hasPool, setHasPool,
  hasSpa, setHasSpa,
  hasFitnessCenter, setHasFitnessCenter,
  hasRoomService, setHasRoomService,
  
  // Activity-specific props
  difficultyLevel, setDifficultyLevel,
  duration, setDuration,
  equipmentNeeded, setEquipmentNeeded,
  maxGroupSize, setMaxGroupSize,
  minAge, setMinAge,
  suitableForChildren, setSuitableForChildren,
  weatherDependent, setWeatherDependent,
  
  // Festival/Event-specific props
  startDate, setStartDate,
  endDate, setEndDate,
  frequency, setFrequency,
  ticketPrice, setTicketPrice,
  categories, setCategories,
  capacity, setCapacity,
  familyFriendly, setFamilyFriendly,
  hasFood, setHasFood,
  
  // Attraction-specific props
  entranceFee, setEntranceFee,
  attractionType, setAttractionType,
  suggestedDuration, setSuggestedDuration,
  exhibits, setExhibits,
  historicalSignificance, setHistoricalSignificance,
  isIndoor, setIsIndoor,
  
  isExistingPlace = false
}: PlaceDetailsProps) {
  // Function to render type-specific fields
  const renderTypeSpecificFields = () => {
    switch (placeType) {
      case 'restaurant':
        return (
          <RestaurantDetails
            mealPriceRange={mealPriceRange}
            setMealPriceRange={setMealPriceRange}
            cuisineType={cuisineType}
            setCuisineType={setCuisineType}
            cuisineSpecialties={cuisineSpecialties}
            setCuisineSpecialties={setCuisineSpecialties}
            dressCode={dressCode}
            setDressCode={setDressCode}
            outdoorSeating={outdoorSeating}
            setOutdoorSeating={setOutdoorSeating}
            isExistingPlace={isExistingPlace}
          />
        );
      case 'hotel':
        return (
          <HotelDetails
            hotelClass={hotelClass}
            setHotelClass={setHotelClass}
            checkInTime={checkInTime}
            setCheckInTime={setCheckInTime}
            checkOutTime={checkOutTime}
            setCheckOutTime={setCheckOutTime}
            hasPool={hasPool}
            setHasPool={setHasPool}
            hasSpa={hasSpa}
            setHasSpa={setHasSpa}
            hasFitnessCenter={hasFitnessCenter}
            setHasFitnessCenter={setHasFitnessCenter}
            hasRoomService={hasRoomService}
            setHasRoomService={setHasRoomService}
            amenities={amenities}
            setAmenities={setAmenities}
            isExistingPlace={isExistingPlace}
          />
        );
      case 'activity':
        return (
          <ActivityDetails
            difficultyLevel={difficultyLevel}
            setDifficultyLevel={setDifficultyLevel}
            duration={duration}
            setDuration={setDuration}
            equipmentNeeded={equipmentNeeded}
            setEquipmentNeeded={setEquipmentNeeded}
            maxGroupSize={maxGroupSize}
            setMaxGroupSize={setMaxGroupSize}
            minAge={minAge}
            setMinAge={setMinAge}
            suitableForChildren={suitableForChildren}
            setSuitableForChildren={setSuitableForChildren}
            weatherDependent={weatherDependent}
            setWeatherDependent={setWeatherDependent}
            bestTimeToVisit={bestTimeToVisit}
            setBestTimeToVisit={setBestTimeToVisit}
            isExistingPlace={isExistingPlace}
          />
        );
      case 'festival':
      case 'event':
        return (
          <FestivalDetails
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            frequency={frequency}
            setFrequency={setFrequency}
            ticketPrice={ticketPrice}
            setTicketPrice={setTicketPrice}
            categories={categories}
            setCategories={setCategories}
            capacity={capacity}
            setCapacity={setCapacity}
            familyFriendly={familyFriendly}
            setFamilyFriendly={setFamilyFriendly}
            hasFood={hasFood}
            setHasFood={setHasFood}
            isExistingPlace={isExistingPlace}
          />
        );
      case 'museum':
      case 'beach':
      case 'park':
      case 'attraction':
      case 'landmark':
      case 'viewpoint':
        return (
          <AttractionDetails
            entranceFee={entranceFee}
            setEntranceFee={setEntranceFee}
            attractionType={attractionType}
            setAttractionType={setAttractionType}
            suggestedDuration={suggestedDuration}
            setSuggestedDuration={setSuggestedDuration}
            exhibits={exhibits}
            setExhibits={setExhibits}
            historicalSignificance={historicalSignificance}
            setHistoricalSignificance={setHistoricalSignificance}
            isIndoor={isIndoor}
            setIsIndoor={setIsIndoor}
            bestTimeToVisit={bestTimeToVisit}
            setBestTimeToVisit={setBestTimeToVisit}
            isExistingPlace={isExistingPlace}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Additional Details</h3>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="address">Address</Label>
          <Input 
            id="address" 
            value={address} 
            onChange={(e) => setAddress(e.target.value)} 
            placeholder="Full address" 
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="url">Website URL</Label>
          <Input 
            id="url" 
            value={url} 
            onChange={(e) => setUrl(e.target.value)} 
            placeholder="https://example.com" 
            className={isExistingPlace ? "bg-gray-50" : ""}
            readOnly={isExistingPlace}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="note">Notes</Label>
          <Textarea 
            id="note" 
            value={note} 
            onChange={(e) => setNote(e.target.value)} 
            placeholder="Any additional information or personal tips" 
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="reservation" 
            checked={requiresReservation} 
            onCheckedChange={setRequiresReservation} 
            disabled={isExistingPlace}
          />
          <Label htmlFor="reservation" className={isExistingPlace ? "text-muted-foreground" : ""}>
            Requires reservation
          </Label>
        </div>
      </div>
      
      {/* Type-specific fields */}
      {renderTypeSpecificFields()}
      
      {/* Common details for all place types */}
      <CommonDetails 
        accessibilityFeatures={accessibilityFeatures}
        setAccessibilityFeatures={setAccessibilityFeatures}
        tags={tags}
        setTags={setTags}
        isExistingPlace={isExistingPlace}
      />
    </div>
  );
}
